/**
 * LoginPage — `/login` route.
 *
 * Extracted from the monolithic `App.tsx`'s `LoginForm` (lines 354–437)
 * with identical UX and copy. POSTs `/sso/issue` → calls
 * `useAuth().login(...)` → redirects to `/launcher` (or the `?from=`
 * return-to path, if present).
 */

import * as React from 'react';
import { Redirect, useLocation, useSearch } from 'wouter';
import { FadeIn } from '@workspace/ui';

import { API_BASE, PLATFORM_API } from '../lib/api';
import { useAuth, type PlatformUser } from '../providers/AuthProvider';
import { useLang, useT } from '../providers/LangProvider';
import { PdplConsentCheckbox, buildConsentPayload } from '@workspace/ui';
import { DemoAccountSelector } from '@workspace/ui/uat';

function pickReturnTo(search: string): string {
  const params = new URLSearchParams(search);
  const from = params.get('from');
  if (!from) return '/launcher';
  // Only allow same-origin absolute paths to avoid open-redirect.
  if (from.startsWith('/') && !from.startsWith('//')) return from;
  return '/launcher';
}

/** B-10: record PDPL consent after successful login. Fire-and-forget — never blocks the redirect. */
async function recordLoginConsent(token: string): Promise<void> {
  const payload = buildConsentPayload();
  await fetch(`${API_BASE}/v1/me/consents/batch`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({
      grants: [
        { channel: 'superall', purpose: 'service', legalBasis: 'contract' as const, ...payload },
      ],
    }),
  }).catch(() => {});
}

export function LoginPage(): React.ReactElement {
  const t = useT();
  const [lang, setLang] = useLang();
  const { token, login } = useAuth();
  const [, setLocation] = useLocation();
  const search = useSearch();
  const returnTo = pickReturnTo(search);

  const [email, setEmail] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [consentGiven, setConsentGiven] = React.useState(false);

  // Already authenticated — bounce.
  if (token) {
    return <Redirect to={returnTo} />;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const res = await fetch(`${PLATFORM_API}/sso/issue`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim(), password }),
      });
      if (!res.ok) {
        setError(t.loginError);
        return;
      }
      const data = (await res.json()) as {
        token: string;
        refreshToken: string;
        user: PlatformUser;
      };
      login(data.token, data.refreshToken, data.user);
      if (consentGiven) void recordLoginConsent(data.token);
      setLocation(returnTo);
    } catch {
      setError(t.loginError);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div
      className="min-h-screen bg-neutral-50 dark:bg-[#0f0a1e] font-sans"
      dir={lang === 'ar' ? 'rtl' : 'ltr'}
    >
      <header className="flex items-center justify-between border-b border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 px-4 py-3">
        <h1
          className="text-xl font-bold text-purple-900 dark:text-purple-300"
          style={{ fontFamily: 'system-ui, sans-serif' }}
        >
          {t.title}
        </h1>
        <button
          type="button"
          onClick={() => setLang(lang === 'en' ? 'ar' : 'en')}
          className="rounded-lg border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 px-3 py-1.5 text-sm text-neutral-600 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-800 focus-visible:ring-2 focus-visible:ring-purple-500 focus-visible:ring-offset-2"
          aria-label={t.langSwitchAria}
        >
          {t.lang}
        </button>
      </header>
      <main className="mx-auto max-w-2xl px-4 py-6">
        <p className="mb-6 text-center text-sm text-neutral-500 dark:text-neutral-400">
          {t.tagline}
        </p>
        <FadeIn delay={0.1}>
          <div className="mx-auto mt-8 max-w-sm rounded-2xl border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 p-6 shadow-sm">
            <h2 className="mb-5 text-center text-lg font-semibold text-neutral-900 dark:text-neutral-100">
              {t.signIn}
            </h2>
            <form onSubmit={handleSubmit} className="flex flex-col gap-4">
              <div className="flex flex-col gap-1">
                <label
                  className="text-sm font-medium text-neutral-700 dark:text-neutral-300"
                  htmlFor="sa-email"
                >
                  {t.emailLabel}
                </label>
                <input
                  id="sa-email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t.emailPlaceholder}
                  dir={lang === 'ar' ? 'rtl' : 'ltr'}
                  className="rounded-lg border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100 px-3 py-2 text-sm outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-200"
                />
              </div>
              <div className="flex flex-col gap-1">
                <label
                  className="text-sm font-medium text-neutral-700 dark:text-neutral-300"
                  htmlFor="sa-password"
                >
                  {t.passwordLabel}
                </label>
                <input
                  id="sa-password"
                  type="password"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={t.passwordPlaceholder}
                  className="rounded-lg border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100 px-3 py-2 text-sm outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-200"
                />
              </div>
              {error && (
                <p
                  role="alert"
                  className="rounded-lg bg-red-50 dark:bg-red-950/30 px-3 py-2 text-xs text-red-600 dark:text-red-400"
                >
                  {error}
                </p>
              )}
              {/* B-10 — PDPL consent gate (Saudi PDPL Art. 6) */}
              <PdplConsentCheckbox
                checked={consentGiven}
                onChange={setConsentGiven}
                lang={lang}
                className="mt-1"
                testId="pdpl-consent-superall"
              />
              <button
                type="submit"
                disabled={loading || !consentGiven}
                aria-busy={loading}
                className="rounded-lg bg-purple-700 px-4 py-2.5 text-sm font-semibold text-white hover:bg-purple-800 disabled:opacity-60 focus-visible:ring-2 focus-visible:ring-purple-500 focus-visible:ring-offset-2"
              >
                {loading ? t.signingIn : t.signInBtn}
              </button>
              {/* GAP-26: password recovery */}
              <a
                href="/superall/forgot-password"
                className="block text-center text-sm text-neutral-500 dark:text-neutral-400 underline underline-offset-2 hover:text-neutral-700 dark:hover:text-neutral-200"
              >
                {t.forgotPassword}
              </a>
            </form>
          </div>
        </FadeIn>

        {/* UAT Demo Selector — visible only when VITE_UAT_DEMO_MODE=true */}
        <DemoAccountSelector
          platform="superall"
          onSelect={(_demoEmail, _demoPassword) => {
            setEmail(_demoEmail);
            setPassword(_demoPassword);
            void (async () => {
              setLoading(true);
              setError(null);
              try {
                const res = await fetch(`${PLATFORM_API}/sso/issue`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ email: _demoEmail.trim(), password: _demoPassword }),
                });
                if (!res.ok) {
                  setError(t.loginError);
                  return;
                }
                const data = (await res.json()) as {
                  token: string;
                  refreshToken: string;
                  user: PlatformUser;
                };
                login(data.token, data.refreshToken, data.user);
                if (consentGiven) void recordLoginConsent(data.token);
                setLocation(returnTo);
              } catch {
                setError(t.loginError);
              } finally {
                setLoading(false);
              }
            })();
          }}
        />
      </main>
    </div>
  );
}
